import { useEffect, useState } from "react";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { hivesApi } from "../../services/hivesApi";
import { rowPosition } from "./hiveRowOrder";

/**
 * Walk a row without going back to the list (#267).
 *
 * The web half of what mobile got in #174. Someone working a yard opens a
 * hive, records what they found, and the next thing they want is the hive
 * standing next to it -- not a list, not the dashboard, and certainly not
 * a scroll to find where they were.
 *
 * Loaded here rather than passed down, because the hive detail page has
 * no reason to know about the rest of the yard and every screen that
 * grows a "just fetch this one extra thing" prop gets harder to reuse.
 * Best-effort: a hive page that cannot fetch its neighbours still shows
 * the hive.
 */
export function HiveRowNav({ hive, onNavigate }) {
  const [siteHives, setSiteHives] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!hive?.site_id) {
      setSiteHives([]);
      return () => { cancelled = true; };
    }
    hivesApi
      .list({ site_id: hive.site_id })
      .then((r) => !cancelled && setSiteHives(r.hives || []))
      .catch(() => !cancelled && setSiteHives([]));
    return () => { cancelled = true; };
  }, [hive?.site_id]);

  if (!siteHives || siteHives.length <= 1) return null;

  const { previous, next, position, total } = rowPosition(siteHives, hive.hive_id);
  if (position === null) return null;

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: "var(--space-2)",
        flexWrap: "wrap", marginBottom: "var(--space-3)",
      }}
    >
      <Button
        size="sm"
        variant="secondary"
        disabled={!previous}
        onClick={() => previous && onNavigate(previous.hive_id)}
      >
        {/* Names the destination. "Previous" alone makes a keeper click to
            find out where they are going. */}
        {previous ? `← Hive ${previous.hive_number}` : "← Start of row"}
      </Button>

      <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        Hive {position} of {total}
        {hive.site_name ? ` at ${hive.site_name}` : ""}
      </span>

      {/* The neighbour's state, so the walk can be interrupted for a good
          reason. Without it you only learn the next hive needs something
          after you have opened it. */}
      {next && !next.last_checked && <Badge tone="danger">Next: never checked</Badge>}
      {next && next.last_checked && next.needs_attention && <Badge tone="warning">Next: due</Badge>}

      <Button
        size="sm"
        variant="secondary"
        style={{ marginLeft: "auto" }}
        disabled={!next}
        onClick={() => next && onNavigate(next.hive_id)}
      >
        {next ? `Hive ${next.hive_number} →` : "End of row →"}
      </Button>
    </div>
  );
}
